import { CloseIconWhite } from '@/components/icons/icons';
import { ExperienceCategory } from '@/generated/graphql';
import { ArrowForward } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import { useExperienceCategory } from '../../hooks/use-experience-category';

import Link from 'next/link';
import { HomepageContent } from '../../../../lib/fetch-content';
import { config } from '@/config/environment';
import AdvocateIcon from '../../assets/advocate-default.png';
import ConnectIcon from '../../assets/connect-default.png';
import ExploreIcon from '../../assets/explore-default.png';
import MakeIcon from '../../assets/make-default.png';
import MoveIcon from '../../assets/move-default.png';
import TasteIcon from '../../assets/taste-default.png';
import { FeaturedPast } from './components/featured-past/featured-past';
import { Upcoming } from './components/upcoming/upcoming';

const categories = {
  [ExperienceCategory.Advocate]: {
    icon: AdvocateIcon,
    label: 'Advocate',
    shortText:
      'Amplify voices! “Advocate” covers Curiko experiences where you can contribute to conversations around oppression and equality.',
  },
  [ExperienceCategory.Connect]: {
    icon: ConnectIcon,
    label: 'Connect',
    shortText:
      'Make a new connection! “Connect” covers Curiko experiences where you can meet others, talk about your ideas, and reflect.',
  },
  [ExperienceCategory.Explore]: {
    icon: ExploreIcon,
    label: 'Explore',
    shortText:
      'Ready to widen your world? “Explore” covers Curiko experiences where you learn about a new place, idea, or interest.',
  },
  [ExperienceCategory.Make]: {
    icon: MakeIcon,
    label: 'Make',
    shortText:
      'Get your creative juices flowing! “Make” covers Curiko experiences where you create things like poems, plays, art pieces, and robots.',
  },
  [ExperienceCategory.Move]: {
    icon: MoveIcon,
    label: 'Move',
    shortText:
      'Shake it up! “Move” covers Curiko experiences where you can move your body in all sorts of ways through arts, sports, and mindful practices.',
  },
  [ExperienceCategory.Taste]: {
    icon: TasteIcon,
    label: 'Taste',
    shortText:
      'Let’s go to flavour town! “Taste” covers Curiko experiences that are all about food.',
  },
};

const DialogTitle = styled(Typography)``;

const HeaderContent = styled(Stack)`
  background-color: ${({ theme }) => theme.palette.common.homepagePurple};
`;

const MainContent = styled(Stack)`
  background-color: ${({ theme }) => theme.palette.common.homepageLightPurple};
  overflow-y: auto;
`;

const TabButton = styled((props: ButtonProps) => (
  <Button
    TouchRippleProps={{
      style: {
        backgroundColor: 'transparent',
      },
    }}
    {...props}
  />
))<{ active: boolean }>`
  border-radius: 0;
  color: ${({ theme, active }) => (active ? theme.palette.common.yellow : '#FDF3DE')};
  border-bottom: ${({ active, theme }) =>
    active ? `2px solid ${theme.palette.common.yellow}` : '2px solid transparent'};
  transition:
    color 0.3s,
    border-bottom 0.3s;

  &:hover {
    background-color: transparent;
    border-bottom: ${({ theme }) => `2px solid ${theme.palette.common.yellow}`};
  }
`;

const UpcomingButton = styled(Link)`
  text-decoration: none;
  background-color: ${({ theme }) => theme.palette.common.yellow};
  padding: ${({ theme }) => theme.spacing(1, 2).toString()};
  color: ${({ theme }) => theme.palette.common.midnight};
  border: none;
  outline: none;
  text-align: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: filter 0.3s;

  &:hover,
  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.palette.common.yellow};
    filter: brightness(0.9);
  }
`;

const PastButton = styled(Link)`
  text-decoration: none;
  background-color: ${({ theme }) => theme.palette.common.homepageVeryLightPurple};
  padding: ${({ theme }) => theme.spacing(1, 2).toString()};
  color: ${({ theme }) => theme.palette.common.midnight};
  border: none;
  outline: none;
  text-align: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: filter 0.3s;

  &:hover,
  &:focus {
    outline: none;
    filter: brightness(0.9);
  }
`;

interface CategoryModalProps {
  homepageContent: HomepageContent;
}

export function CategoryModal({ homepageContent }: CategoryModalProps) {
  const { category, closeModal, tab, setTab } = useExperienceCategory();
  const archiveMode = config.platformMode === 'archive';
  const currentTab = archiveMode ? 'past' : tab;

  const categoryContent = categories[category || ExperienceCategory.Advocate];

  const pastExists =
    (homepageContent?.featuredPastExperiences || []).filter(
      experience => experience.category === category,
    )?.length > 0;

  const content = category ? (
    <>
      <HeaderContent gap={2} pt={2} px={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={1}>
            <Box>
              <Image
                src={categories[category || ExperienceCategory.Advocate]['icon']}
                alt={`${category} Icon`}
                width={72}
                height={72}
              />
            </Box>
            <DialogTitle
              variant="h1"
              color="white"
              sx={{
                fontSize: {
                  xs: '24px',
                  sm: '50px',
                },
              }}
            >
              {categories[category || ExperienceCategory.Advocate]['label']}
            </DialogTitle>
          </Stack>
          <Box flexGrow={1} />
          <IconButton onClick={closeModal}>
            <CloseIconWhite />
          </IconButton>
        </Stack>
        <Typography color="white">{categoryContent.shortText}</Typography>
        {/* tabs */}
        <Stack direction="row" spacing={2}>
          {!archiveMode && (
            <TabButton
              variant="text"
              active={currentTab === 'upcoming'}
              onClick={() => setTab('upcoming')}
            >
              Upcoming
            </TabButton>
          )}
          {pastExists && (
            <TabButton
              variant="text"
              color="secondary"
              active={currentTab === 'past'}
              onClick={() => setTab('past')}
            >
              Featured past experiences
            </TabButton>
          )}
        </Stack>
      </HeaderContent>
      <MainContent>
        {currentTab === 'upcoming' && <Upcoming />}
        {currentTab === 'past' && <FeaturedPast homepageContent={homepageContent} />}
      </MainContent>
      {!archiveMode && currentTab === 'upcoming' && (
        <UpcomingButton href={`/explore?category=${category}`} passHref>
          See all {categoryContent.label} experiences <ArrowForward fontSize="inherit" />
        </UpcomingButton>
      )}
      {!archiveMode && currentTab === 'past' && pastExists && (
        <PastButton href={`/request-experience`} passHref>
          Request a past experience
        </PastButton>
      )}
    </>
  ) : null;

  return (
    <Dialog
      open={!!category}
      onClose={() => {
        closeModal();
        // clear focus from the button that opened the modal
        (document.activeElement as any)?.blur();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { backgroundColor: theme => theme.palette.common.midnight } }}
    >
      {content}
    </Dialog>
  );
}
